import { EApplicantType, ECreditApplicationStatus, EDecision, EExternalStepStatus } from '@if/core/types'
import { copyMessage } from '@if/core/utils'
import CopyAllIcon from '@mui/icons-material/CopyAll'
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined'
import { Button, Grid, Typography } from '@mui/material'
import { useSideEffect } from '@src/data/store/effects/side-effects'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IconButtonWithTooltip from '../../../components/IconButtonWithTooltip'
import { useAppSelector } from '../../../data/store'
import { creditEffects, creditSelectors } from '../../../data/store/CreditApplication'
import { CreditApplication, RequiredExternalStep, User } from '../../../data/types'
import FlinksHistoryDialog from './FlinksHistoryDialog'

interface FlinksActionsSectionProps {
  applicantType: EApplicantType
  handleSaveBankAccountRequest: (applicant: EApplicantType) => void
  handleResetBankAccountRequest: (applicant: EApplicantType) => void
  requiredExternalStep: RequiredExternalStep | undefined
  user: User | null
  creditApplication: CreditApplication
  editDisabled: boolean
  worksheetIsApproved: boolean
  creditApplicationId: string
  showHistoryDialog?: boolean
}

const FlinksActionsSection = ({
  applicantType: applicantType,
  handleSaveBankAccountRequest,
  handleResetBankAccountRequest,
  requiredExternalStep,
  user,
  creditApplication,
  editDisabled,
  worksheetIsApproved,
  creditApplicationId,
  showHistoryDialog = true,
}: FlinksActionsSectionProps) => {
  const { t } = useTranslation()
  const dispatchEffect = useSideEffect()
  const INCOME_FLINKS_DIALOG = 'OpenIncomeFlinksDialog'
  const [currentOpenDialog, setCurrentOpenDialog] = useState<string>('')

  const isLoadingHistory = useAppSelector(creditSelectors.isLoadingIncomesRevision)
  const externalReportedIncomesForApplicant = useAppSelector(creditSelectors.getFlinksIncomesForApplicant)
  const externalReportedIncomesForCoApplicant = useAppSelector(creditSelectors.getFlinksIncomesForCoApplicant)

  const status = requiredExternalStep?.status

  const applicationIsDraft = creditApplication?.status === ECreditApplicationStatus.Draft

  const externalReportedIncome = () => {
    return applicantType === EApplicantType.Applicant
      ? externalReportedIncomesForApplicant
      : externalReportedIncomesForCoApplicant
  }

  const closeDialogs = useCallback(() => {
    setCurrentOpenDialog('')
  }, [])

  const flinksButtonIsDisabled = (): boolean =>
    !user?.rights.canEditCreditApp ||
    applicationIsDraft ||
    creditApplication?.finalCreditDecision?.decision !== EDecision.Approved ||
    editDisabled

  const getFlinksButtonLabel = () => {
    if (status !== EExternalStepStatus.Completed) {
      return status ? t('incomeSection.resendLink') : 'FLINKS'
    }
    return t('incomeSection.flinksDashboard')
  }

  return (
    <>
      <Grid container direction="row" alignItems="center" columnSpacing={1}>
        <Grid>
          <Button
            sx={{ mr: 1 }}
            variant="outlined"
            onClick={() => handleSaveBankAccountRequest(applicantType)}
            disabled={flinksButtonIsDisabled() || worksheetIsApproved}
          >
            {getFlinksButtonLabel()}
          </Button>
        </Grid>

        <Grid>
          <Typography variant="body2" component="div" sx={{ border: '1px solid', borderRadius: 1, p: 0.85, mr: 1 }}>
            {t('incomeSection.flinksStatus')} :{' '}
            {requiredExternalStep?.status
              ? t(`EExternalStepStatus.${requiredExternalStep.status}`, { ns: 'enum' })
              : t('EExternalStepStatus.notStarted', { ns: 'enum' })}
          </Typography>
        </Grid>

        <Grid>
          <Button
            sx={{ mr: 1 }}
            variant="outlined"
            onClick={() => handleResetBankAccountRequest(applicantType)}
            disabled={status !== EExternalStepStatus.Completed}
          >
            {t('incomeSection.reset')}
          </Button>
        </Grid>

        <Grid>
          <IconButtonWithTooltip
            tooltip={t('common.hoverCopyFlinksLink')}
            icon={<CopyAllIcon />}
            onClick={() => {
              const url = requiredExternalStep?.extraProperties.bankAccountRequestLink
              if (url) {
                copyMessage(url).catch(reportErrorToConsole)
              }
            }}
            disabled={flinksButtonIsDisabled() || worksheetIsApproved}
          />
        </Grid>
        {externalReportedIncome() && (
          <Grid>
            <IconButtonWithTooltip
              tooltip={t('incomeSection.incomeFlinkHistory')}
              icon={<NewReleasesOutlinedIcon fontSize="medium" />}
              onClick={() => {
                dispatchEffect(
                  creditEffects.getIncomesHistory(creditApplicationId, creditApplication.financingProgramId),
                ).catch(reportErrorToConsole)
                setCurrentOpenDialog(INCOME_FLINKS_DIALOG)
              }}
            />
          </Grid>
        )}
      </Grid>
      {showHistoryDialog && externalReportedIncome() && (
        <FlinksHistoryDialog
          open={currentOpenDialog === INCOME_FLINKS_DIALOG}
          onClose={closeDialogs}
          isLoadingHistory={isLoadingHistory}
          flinksData={externalReportedIncome()!}
        />
      )}
    </>
  )
}

export default FlinksActionsSection
